import type { CardId } from "@/types/card"

/*
 * UI patterns, and the map from a card to the ones that show it in a product.
 *
 * This is the content behind "See it in action" in the capability detail
 * overlay. It has two halves, the same shape as `techStack.ts`:
 *
 *   PATTERNS       the library — one entry per pattern, with the one-line
 *                  "use it when" the overlay shows and the surface/layer it
 *                  carries for later.
 *   CARD_PATTERNS  the match — which patterns each of the 24 active cards points
 *                  at, plus a fit. This is the data my coworker built in Figma
 *                  (AI Club › "Capability mapping — AI Cards → UI Patterns").
 *
 * Pattern copy (`use`/`avoid`) is ported verbatim from the `ai-ui-patterns`
 * handoff, which is the authority on it. The handoff is the earlier 26-pattern
 * cut, so it has no copy for the FIVE patterns the library added in Oct 2026 —
 * `mixed`, `readaloud`, `monitoring`, `planreview`, `assumptions`. Their copy
 * here is DRAFT, written in the handoff's voice and marked `draft: true`; it has
 * not been reviewed and must be before it ships. (Same footing as the notice at
 * the top of `deckExamples.ts`.)
 *
 * No interactive demos yet. The handoff animates every pattern; porting those is
 * a later pass. A demo will register against `Pattern.id`, which is why the id is
 * the handoff's own — so the seam is already in place.
 */

export type PatternSurface = "chat" | "embedded" | "both"

/** Core interaction (trigger/receive) vs. trust & control (check/steer/undo). */
export type PatternLayer = "core" | "trust"

export interface Pattern {
  /** The handoff's id — also the key a future interactive demo registers under. */
  id: string
  name: string
  surface: PatternSurface
  /** Carried for later grouping; not shown in the overlay yet. */
  layer: PatternLayer
  /** One line: when this pattern earns its place. The line the overlay shows. */
  use: string
  /** One line: when to reach for something else. Carried for depth; not shown yet. */
  avoid: string
  /** Copy is unverified draft awaiting review (the five Oct-2026 additions). */
  draft?: boolean
}

/* ---------------------------------------------------------------- library */

const PATTERNS: Record<string, Pattern> = {
  // Core interaction
  stream: {
    id: "stream",
    name: "Streaming response",
    surface: "both",
    layer: "core",
    use: "Output takes more than a second to finish, so people see progress right away.",
    avoid:
      "The output is short, or it has to be validated as a whole before anyone sees it (like structured data).",
  },
  starters: {
    id: "starters",
    name: "Starter prompts",
    surface: "chat",
    layer: "core",
    use: "An open-ended input is empty and people don't know what to ask.",
    avoid: "The task is narrow and the input already explains itself.",
  },
  ghost: {
    id: "ghost",
    name: "Inline suggestion",
    surface: "embedded",
    layer: "core",
    use: "People write in context and the next words are predictable.",
    avoid: "Suggestions are long or often wrong. They break the writer's flow.",
  },
  selbar: {
    id: "selbar",
    name: "Selection actions",
    surface: "embedded",
    layer: "core",
    use: "People want to act on one specific part of existing content.",
    avoid: "Content isn't editable, or most use happens on touch where selecting text is awkward.",
  },
  variants: {
    id: "variants",
    name: "Multiple options",
    surface: "both",
    layer: "core",
    use: "Taste matters and there is no single right answer.",
    avoid: "Comparing options costs more effort than editing one good draft.",
  },
  tldr: {
    id: "tldr",
    name: "Summary card",
    surface: "embedded",
    layer: "core",
    use: "Long content that people skim before deciding whether to read it.",
    avoid: "The content is already short, or every detail matters (legal, medical).",
  },
  tags: {
    id: "tags",
    name: "Suggested tags",
    surface: "embedded",
    layer: "core",
    use: "Items need consistent labels for sorting, filtering or routing.",
    avoid: "A wrong label is costly and hard to spot. Use a review queue instead.",
  },
  autofill: {
    id: "autofill",
    name: "Smart fill",
    surface: "embedded",
    layer: "core",
    use: "Data lives in a document or image and needs to land in a form.",
    avoid: "Fields are few and faster to type than to review.",
  },
  answer: {
    id: "answer",
    name: "Answer above results",
    surface: "embedded",
    layer: "core",
    use: "Questions have a clear answer somewhere in your content.",
    avoid: "Queries are navigational and people want a specific page, not an answer.",
  },
  because: {
    id: "because",
    name: "“Because you…” row",
    surface: "embedded",
    layer: "core",
    use: "Recommendations need a reason people recognize from their own behavior.",
    avoid: "The signal is sensitive or would feel invasive when shown back.",
  },
  anomaly: {
    id: "anomaly",
    name: "Insight callout",
    surface: "embedded",
    layer: "core",
    use: "A chart or dashboard hides something worth acting on.",
    avoid: "Callouts fire often. People learn to ignore them.",
  },
  nudge: {
    id: "nudge",
    name: "Proactive suggestion",
    surface: "embedded",
    layer: "core",
    use: "The AI notices something useful before people ask, and acting on it is one click.",
    avoid: "It interrupts focused work or can't be dismissed for good.",
  },
  plan: {
    id: "plan",
    name: "Plan & progress",
    surface: "both",
    layer: "core",
    use: "A task has several steps and people need to see what is happening.",
    avoid: "The task is a single fast action. Just show the result.",
  },
  clarify: {
    id: "clarify",
    name: "Clarifying question",
    surface: "chat",
    layer: "core",
    use: "A request is ambiguous and guessing wrong would waste time.",
    avoid: "A sensible default exists. Act on it and let people change it.",
  },
  copilot: {
    id: "copilot",
    name: "Side-panel assistant",
    surface: "chat",
    layer: "core",
    use: "People need help with what's on screen without leaving it.",
    avoid: "Screen space is tight, or a single inline action would do.",
  },
  voice: {
    id: "voice",
    name: "Live transcript",
    surface: "both",
    layer: "core",
    use: "People speak instead of type and need to see they were understood.",
    avoid: "The environment is noisy or private, with no typing fallback.",
  },
  detect: {
    id: "detect",
    name: "Object highlights",
    surface: "embedded",
    layer: "core",
    use: "People need to see what the AI found and where in an image.",
    avoid: "The image is small or crowded and boxes would hide what matters.",
  },
  translate: {
    id: "translate",
    name: "Translation toggle",
    surface: "embedded",
    layer: "core",
    use: "Content arrives in another language and people may want the original.",
    avoid: "Nuance or legal meaning depends on exact wording.",
  },

  // Trust & control
  regen: {
    id: "regen",
    name: "Regenerate & versions",
    surface: "both",
    layer: "trust",
    use: "Output varies run to run and people may prefer an earlier take.",
    avoid: "Results should be deterministic, like calculations or extracted data.",
  },
  cite: {
    id: "cite",
    name: "Citations",
    surface: "both",
    layer: "trust",
    use: "Answers come from sources that people may need to verify.",
    avoid: "The output is creative and has no source to point to.",
  },
  conf: {
    id: "conf",
    name: "Confidence signal",
    surface: "both",
    layer: "trust",
    use: "Accuracy varies and people should know when to double-check.",
    avoid: "Showing raw percentages to people who can't act on them. Use plain words.",
  },
  approve: {
    id: "approve",
    name: "Approval step",
    surface: "both",
    layer: "trust",
    use: "The AI is about to do something hard to undo: send, pay, delete, publish.",
    avoid: "The action is low risk and reversible. Confirming every step adds friction.",
  },
  diff: {
    id: "diff",
    name: "Review changes",
    surface: "embedded",
    layer: "trust",
    use: "The AI edits existing content and people must see exactly what changed.",
    avoid: "The rewrite is total. A before and after view works better.",
  },
  why: {
    id: "why",
    name: "“Why this?” explanation",
    surface: "both",
    layer: "trust",
    use: "A decision affects people and they deserve to know the main reason.",
    avoid: "The explanation would be vague (“based on your activity”) and add nothing.",
  },
  undo: {
    id: "undo",
    name: "Undo AI action",
    surface: "both",
    layer: "trust",
    use: "The AI changed something for people and they need a fast way back.",
    avoid: "The action can't be reversed. Ask for approval before, instead.",
  },

  // Added Oct 2026 — DRAFT copy, unreviewed. See the file header.
  mixed: {
    id: "mixed",
    name: "Mixed input",
    surface: "both",
    layer: "core",
    use: "People can type, talk, or show a picture, and forcing one mode throws away the easiest.",
    avoid: "The input is one well-defined field where a single mode is clearly fastest.",
    draft: true,
  },
  readaloud: {
    id: "readaloud",
    name: "Read-aloud",
    surface: "both",
    layer: "core",
    use: "People would rather listen than read, on the move, hands busy, or for accessibility.",
    avoid: "The content is visual or scannable, where a glance beats hearing it played back.",
    draft: true,
  },
  monitoring: {
    id: "monitoring",
    name: "Ongoing monitoring",
    surface: "embedded",
    layer: "core",
    use: "Something changes over time, and people need to know when it crosses a line without watching it themselves.",
    avoid:
      "Nothing much changes between checks, or every change matters equally, so a constant feed is just noise.",
    draft: true,
  },
  planreview: {
    id: "planreview",
    name: "Review plan before running",
    surface: "both",
    layer: "trust",
    use: "The AI is about to run several steps, and people need to see and approve the plan before any of it runs.",
    avoid: "The steps are trivial and reversible, so approving a plan is friction with no payoff.",
    draft: true,
  },
  assumptions: {
    id: "assumptions",
    name: "Stated assumptions",
    surface: "both",
    layer: "trust",
    use: "An answer rests on assumptions the AI had to make, and people can only trust it if they can see them.",
    avoid: "The answer is grounded and exact, with no assumptions worth spelling out.",
    draft: true,
  },
}

/* ------------------------------------------------------------- the match */

export type PatternFit = "strong" | "partial"

export interface CardPatternSet {
  /** Pattern ids, in the order Figma lists them (lead pattern first). */
  patternIds: readonly string[]
  fit: PatternFit
  /** Why the fit is only partial. Set on partial cards, shown beside the list. */
  fitNote?: string
}

/*
 * Keyed by the 24 ACTIVE card ids. Retired ids (e.g. `f1c4`, `f4c2`) are absent
 * on purpose — they are off the current deck, so the browser never opens them;
 * `patternsForCard` answers null and the overlay falls back to "coming soon".
 */
const CARD_PATTERNS: Record<CardId, CardPatternSet> = {
  // Understand the world
  f1c1: { patternIds: ["detect", "tags", "conf"], fit: "strong" },
  f1c2: { patternIds: ["voice", "translate"], fit: "strong" },
  f1c5: { patternIds: ["mixed", "autofill", "voice"], fit: "strong" },
  f1c3: { patternIds: ["autofill", "tags", "tldr"], fit: "strong" },

  // Communicate & connect
  f2c1: { patternIds: ["tldr", "stream", "regen"], fit: "strong" },
  f2c2: { patternIds: ["readaloud"], fit: "strong" },
  f2c3: { patternIds: ["copilot", "clarify", "starters"], fit: "strong" },
  f2c4: { patternIds: ["translate", "selbar", "diff"], fit: "strong" },

  // Think & reason
  f3c1: { patternIds: ["answer", "cite"], fit: "strong" },
  f3c2: { patternIds: ["plan", "planreview", "clarify"], fit: "strong" },
  f3c3: { patternIds: ["anomaly", "monitoring", "conf", "why"], fit: "strong" },
  f3c4: { patternIds: ["variants", "regen"], fit: "strong" },

  // Take action
  f4c1: { patternIds: ["approve", "undo"], fit: "strong" },
  f4c5: { patternIds: ["monitoring", "plan", "anomaly"], fit: "strong" },
  f4c3: { patternIds: ["planreview", "plan", "approve"], fit: "strong" },
  f4c4: {
    patternIds: ["planreview", "plan", "approve"],
    fit: "partial",
    fitNote: "Mostly backend work — the UI shows what will run where.",
  },

  // Explain & guide
  f6c1: { patternIds: ["why", "cite"], fit: "strong" },
  f6c2: { patternIds: ["nudge", "because"], fit: "strong" },
  f6c3: { patternIds: ["cite", "assumptions", "conf"], fit: "strong" },
  f6c4: { patternIds: ["assumptions", "clarify"], fit: "strong" },

  // Collaborate
  f7c1: { patternIds: ["ghost", "selbar", "variants"], fit: "strong" },
  f7c2: { patternIds: ["anomaly", "variants", "why"], fit: "strong" },
  f7c3: { patternIds: ["copilot", "mixed", "starters"], fit: "strong" },
  f7c4: { patternIds: ["approve", "variants", "assumptions"], fit: "strong" },
}

export interface CardPatterns {
  fit: PatternFit
  fitNote?: string
  patterns: Pattern[]
}

/**
 * The patterns that show a card in a product, resolved to full entries.
 *
 * Null for a card the mapping does not cover (a retired id), so the caller can
 * fall back rather than render an empty section.
 */
export function patternsForCard(id: CardId): CardPatterns | null {
  const set = CARD_PATTERNS[id]
  if (!set) return null
  return {
    fit: set.fit,
    fitNote: set.fitNote,
    patterns: set.patternIds.map((patternId) => PATTERNS[patternId]).filter(Boolean),
  }
}
